import React, { useContext } from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import {
    type PersonalizationClient,
    PersonalizationContext,
} from "@foundation/personalization";

import ZoneModule from "~components/modules/zoneModule";
import {
    getRenderingData,
    zonesWithColumns,
    gridZonesWithColumns,
} from "~components/shared/zones/utility";
import useRedux from "~redux/hooks";
import {
    type AdModule,
    type CurationZone,
    type HydratedCurationModule,
    type VariationComponent,
    ZoneType,
} from "~types/types";
import getComponentName from "~utils/getComponentName";

import styles from "./zone.module.css";

interface ZoneProps {
    preview: boolean;
    zone: CurationZone;
    className?: string;
}

export const excludeModuleContentFromPersonalization = (
    personalizationClient?: PersonalizationClient | null,
    module?: VariationComponent
) => {
    if (!personalizationClient || !module || !module.items) {
        return;
    }
    const contentToExclude = module.items
        .filter((item) => item.id)
        .map((item) => {
            return {
                resourceType: item.type || "",
                contentId: item.id,
            };
        });

    personalizationClient.exclude(contentToExclude);
};

export const GridZone = ({ className, preview, zone }: ZoneProps) => {
    const { personalizationClient } = useContext(PersonalizationContext);
    const { useAppSelector } = useRedux();
    const modules = useAppSelector((state) => state.modulesById);

    const isHfs = zone.name?.match?.(/hfs/gi);
    const zoneModuleIncrement = isHfs ? 0 : 1;

    return (
        <section
            className={className}
            data-zoneid={zone.name}
            data-zonekind={zone.kind}
            data-component="zone"
        >
            {zone.columns.map((column, columnIndex) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const lastModuleInColumn = column.modules.findLast(
                    (moduleInList: HydratedCurationModule | AdModule) => {
                        const hydratedModule = modules[moduleInList.id];

                        return (
                            hydratedModule?.id &&
                            !isShadowModule(hydratedModule) &&
                            !isAdModule(hydratedModule)
                        );
                    }
                );

                return (
                    <section
                        key={`${zone.name}-column${columnIndex}`}
                        className={
                            styles[gridZonesWithColumns[zone.kind][columnIndex]]
                        }
                        data-column={columnIndex + 1}
                    >
                        {column.modules.map((module, i) => {
                            const renderingData = getRenderingData(
                                columnIndex,
                                module,
                                zone,
                                true
                            );

                            const moduleById = modules[module.id];

                            const isLastInColumn =
                                module.id === lastModuleInColumn?.id;

                            const moduleWithRenderingData = {
                                ...moduleById,
                                ...renderingData,
                                moduleIndex: i + zoneModuleIncrement,
                                zoneName: zone.name,
                                isLastInColumn,
                            } as VariationComponent;

                            excludeModuleContentFromPersonalization(
                                personalizationClient,
                                moduleWithRenderingData
                            );

                            return (
                                <ZoneModule
                                    key={module.id}
                                    module={moduleWithRenderingData}
                                    preview={preview}
                                    renderingData={renderingData}
                                />
                            );
                        })}
                    </section>
                );
            })}
        </section>
    );
};

export const Zone = ({ className, preview, zone }: ZoneProps) => {
    const { personalizationClient } = useContext(PersonalizationContext);
    const { useAppSelector } = useRedux();
    const modules = useAppSelector((state) => state.modulesById);
    const isHfs = zone.name?.match?.(/hfs/gi);
    const zoneModuleIncrement = isHfs ? 0 : 1;

    const classNames = createClasses([
        styles.zone,
        styles[zone.kind],
        className,
    ]);

    return (
        <section
            className={classNames}
            data-zoneid={zone.name}
            data-zonekind={zone.kind}
            data-component="zone"
        >
            {zone.columns.map((column, columnIndex) => (
                <section
                    key={`${zone.name}-column${columnIndex}`}
                    className={styles[zonesWithColumns[zone.kind][columnIndex]]}
                    data-column={columnIndex + 1}
                >
                    {(column.modules || []).map((module, i) => {
                        const renderingData = getRenderingData(
                            columnIndex,
                            module,
                            zone,
                            false
                        );
                        const moduleById = modules[module.id];

                        const moduleWithRenderingData = {
                            ...moduleById,
                            ...renderingData,
                            moduleIndex: i + zoneModuleIncrement,
                            zoneName: zone.name,
                        } as VariationComponent;

                        excludeModuleContentFromPersonalization(
                            personalizationClient,
                            moduleWithRenderingData
                        );

                        return (
                            <ZoneModule
                                key={module.id}
                                module={moduleWithRenderingData}
                                preview={preview}
                                renderingData={renderingData}
                            />
                        );
                    })}
                </section>
            ))}
        </section>
    );
};

function isShadowModule(module: HydratedCurationModule | AdModule) {
    return Boolean((module as HydratedCurationModule).duplicateOf);
}

function isAdModule(module: HydratedCurationModule | AdModule) {
    return module.type === "ad";
}

export function getTrendingRecordsModuleIndex(zone: CurationZone) {
    if (zone.kind !== ZoneType.Righty) {
        return -1;
    }

    return zone.columns[1]?.modules?.findIndex(
        ({ variation, type }) =>
            getComponentName(variation, type) === "StoryListTrendingRecords"
    );
}

export function getTrendingRecordsShadowModuleIndex(zone: CurationZone) {
    if (zone.kind !== ZoneType.Righty) {
        return -1;
    }

    return zone.columns[0]?.modules?.findIndex(
        ({ variation, type }) =>
            getComponentName(variation, type) === "StoryListTrendingRecords"
    );
}
